import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { Points, textSize } from '@styles';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { getHelpContent } from '@modules/get-data';
import { getTimeDuration, typographyText } from '@libs';
import { HELP_CENTER, OVERAGE_READING_TIME, IS_CLIENT } from '@constants';
import { TimeRanges } from './time-ranges';
export const ArticleCard = ({
  locale,
  id,
  cover,
  caption,
  description,
  time,
  slug,
  sectionUrl,
  category,
  bgcolor,
  isFixedHeight = false,
}) => {
  const [temporeDescription, setTemporeDescription] = useState();
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;

    // Сработает, даже если картинка в кеше и onLoad не вызовется
    if (img?.complete && img?.naturalWidth !== 0) {
      setLoaded(true);
    } else {
      // На случай, если она ещё не загружена
      const handleLoad = () => setLoaded(true);
      img?.addEventListener('load', handleLoad);

      return () => {
        img?.removeEventListener('load', handleLoad);
      };
    }
  }, [cover]);

  useEffect(() => {
    if (description.length === 0 && IS_CLIENT) {
      const fetchDescription = async () => {
        try {
          const res = await getHelpContent({ section: sectionUrl, category: category, article: slug });

          if (res && res.description) {
            setTemporeDescription(res.description);
          } else {
            console.warn('Описание не найдено в ответе:', res);
          }
        } catch (error) {
          console.error('Ошибка загрузки описания статьи:', error);
        }
      };
      fetchDescription();
    }
  }, []);

  return (
    <Article key={id} bgColor={bgcolor}>
      <Link
        href={
          category === undefined
            ? `/${HELP_CENTER.MAIN}/${sectionUrl}/${slug}`
            : `/${HELP_CENTER.MAIN}/${sectionUrl}/${category}/${slug}`
        }
        passHref>
        <a aria-label={caption}>
          <div className="image-wrapper">
            <img
              ref={imgRef}
              src={cover?.length > 0 ? `${cover}` : `/images/help/placeholder.png`}
              alt={caption}
              key={`cover-${id}`}
              onLoad={() => setLoaded(true)}
              onError={() => console.warn('Image failed to load:', cover)}
            />
          </div>
          <Content isFixedHeight={isFixedHeight}>
            <Title>{typographyText(caption)}</Title>
            <TimeRanges time={time ?? OVERAGE_READING_TIME} locale={locale} />
            <Description>{typographyText(description) || temporeDescription}</Description>
            {}
          </Content>
        </a>
      </Link>
    </Article>
  );
};

const Article = styled.article`
  a {
    ${Mixins.resetLink()};
    display: flex;
    flex-direction: column;
    width: 340px;
    height: 100%;
    border: 1px solid #e1e1e1;

    @media ${Points.tablet_landscape} {
      width: 312px;
      min-height: 520px;
    }

    @media ${Points.laptop_wide} {
      width: 305px;
      min-height: 525px;
    }

    @media ${Points.fullhd} {
      width: 330px;
      min-height: 582px;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
      border-color: ${Colors.main_blue};
      box-shadow: 0px 8px 24px -4px #29308614, 0px 6px 12px -6px #2930861f;
      }
    }

    &:active {
      border-color: ${Colors.main_blue};
      box-shadow: none;
    }
  }

  & div.image-wrapper {
    width: 100%;
    height: 230px;
    background-color: ${(p) => p.bgColor || Colors.main_beige};
    position: 'relative';
    overflow: 'hidden';
    position: 'absolute';
    top: 0;
    left: 0;
    objectFit: 'cover';
    opacity: loaded ? 1 : 0;
    transition: 'opacity 0.6s ease-in-out';

    @media ${Points.fullhd}  {
      height: 250px;
    }
  }

  & img {
    width: 100%;
    height: 230px;
    overflow: revert !important;
    background-color: ${(p) => p.bgColor || Colors.main_beige};

    @media ${Points.fullhd} {
      height: 250px;
    }
  }
`;

const Content = styled.div`
  display: flex;
  height: 100%;
  min-height: ${(p) => (p.isFixedHeight ? '250px' : 'auto')};
  flex-direction: column;
  flex-grow: revert;
  padding: 25px 20px 20px;

  @media ${Points.tablet_landscape} {
    padding-top: 30px;
    min-height: ${(p) => (p.isFixedHeight ? '290px' : 'auto')};
  }
`;

const Title = styled.h2`
  ${textSize('card_header')};
  color: ${Colors.main_dark};
  font-weight: 300;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Description = styled.p`
  display: flex;
  justify-content: end;
  flex-direction: column;
  margin-top: auto;
  padding-top: 30px;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media ${Points.tablet_landscape} {
    padding-top: 40px;
  }
`;
